import { MEDALS, type Medal } from "@seagames/contracts";
import { useTranslation } from "react-i18next";
import { formatCalendarDay, formatInteger } from "../../lib/i18n/format";
import { MEDAL_COLOR } from "../medals/medal-chip";
import { ticks } from "./scale";
import { TipLayer, TipRow, useTip } from "./tooltip";

export interface DayColumn {
	/** `YYYY-MM-DD`, a games day. */
	day: string;
	awarded: Record<Medal, number>;
	/** Medals the day's events expect — the "due" tick. */
	due: number;
}

/**
 * Medals by day: one column per games day, awarded gold / silver / bronze
 * stacked from the baseline (gold at the foot), 2px surface gaps, the top
 * rounded; a short ink tick at what the day's events EXPECT ("due"). Today's
 * column sits on a yellow wash; the selected day is outlined. Every column is
 * a button (the run sheet follows it) and shows its numbers on hover / focus.
 * Columns are ≤ 1.5rem wide (the band's leftover is air); a long calendar
 * scrolls sideways.
 */
export function DayColumns({
	days,
	today,
	selected,
	onSelect,
}: {
	days: readonly DayColumn[];
	today: string | null;
	selected?: string | undefined;
	onSelect?: (day: string) => void;
}) {
	const { t } = useTranslation("charts");
	const tm = useTranslation("medals").t;
	const tip = useTip();
	const totals = days.map((d) => d.awarded.GOLD + d.awarded.SILVER + d.awarded.BRONZE);
	const axis = ticks(Math.max(1, ...totals, ...days.map((d) => d.due)));
	const top = axis[axis.length - 1] ?? 1;
	const pct = (n: number) => `${(n / top) * 100}%`;

	const content = (d: DayColumn, total: number) => (
		<div className="flex min-w-[10rem] flex-col gap-[0.125rem]">
			<p className="mb-[0.25rem] font-bold">{formatCalendarDay(d.day)}</p>
			{MEDALS.map((m) => (
				<TipRow key={m} color={MEDAL_COLOR[m]} label={tm(`medal.${m}`)} value={formatInteger(d.awarded[m])} />
			))}
			<TipRow label={t("days.awarded")} value={formatInteger(total)} strong />
			<TipRow label={t("days.due")} value={formatInteger(d.due)} />
		</div>
	);

	return (
		<div className="flex min-w-0 gap-[0.5rem]">
			{/* The y axis: tick labels only; the gridlines are drawn across the plot. */}
			<div aria-hidden="true" className="relative h-[10rem] w-[1.75rem] shrink-0 font-mono text-[0.6875rem] text-muted">
				{axis.map((v) => (
					<span key={v} className="absolute right-0 translate-y-1/2 tabular-nums" style={{ bottom: pct(v) }}>
						{formatInteger(v)}
					</span>
				))}
			</div>
			<div className="min-w-0 flex-1 overflow-x-auto pb-[0.25rem]">
				<div className="relative flex min-w-max" style={{ minWidth: "100%" }}>
					<div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[10rem]">
						{axis.map((v) => (
							<span key={v} className="absolute inset-x-0 h-px bg-rule" style={{ bottom: pct(v) }} />
						))}
					</div>
					{days.map((d, i) => {
						const total = totals[i] ?? 0;
						const isToday = d.day === today;
						const isSelected = d.day === selected;
						const stacked = MEDALS.filter((m) => d.awarded[m] > 0);
						return (
							<button
								key={d.day}
								type="button"
								onClick={() => onSelect?.(d.day)}
								onPointerMove={(e) => tip.at(e, content(d, total))}
								onPointerLeave={tip.hide}
								onFocus={(e) => tip.under(e.currentTarget, content(d, total))}
								onBlur={tip.hide}
								aria-pressed={onSelect ? isSelected : undefined}
								aria-label={t("days.columnLabel", {
									day: formatCalendarDay(d.day),
									gold: d.awarded.GOLD,
									silver: d.awarded.SILVER,
									bronze: d.awarded.BRONZE,
									due: d.due,
								})}
								className={`group relative flex min-w-[2.25rem] flex-1 cursor-pointer flex-col items-center rounded-btn ${isToday ? "bg-cursor-wash" : "hover:bg-paper"} ${
									isSelected ? "outline-2 outline-board -outline-offset-2" : ""
								}`}
							>
								<span className="relative flex h-[10rem] w-full items-end justify-center">
									<span className="flex w-full max-w-[1.5rem] flex-col-reverse gap-[2px]" style={{ height: pct(total) }}>
										{stacked.map((m, j) => (
											<span
												key={m}
												className={`w-full ${j === stacked.length - 1 ? "rounded-t-[4px]" : ""}`}
												style={{ flexGrow: d.awarded[m], flexBasis: 0, background: MEDAL_COLOR[m] }}
											/>
										))}
									</span>
									{d.due > 0 && (
										<span aria-hidden="true" className="absolute left-1/2 h-[2px] w-[1.25rem] -translate-x-1/2 translate-y-1/2 rounded-full bg-ink/70" style={{ bottom: pct(d.due) }} />
									)}
								</span>
								<span className={`mt-[0.25rem] font-mono text-[0.6875rem] leading-tight ${isToday ? "font-black text-ink" : "text-muted"}`}>{dayOfMonth(d.day)}</span>
								<span className={`pb-[0.25rem] text-[0.625rem] uppercase leading-tight ${isToday ? "font-bold text-ink" : "text-muted"}`}>{weekdayOf(d.day)}</span>
							</button>
						);
					})}
				</div>
			</div>
			<TipLayer tip={tip} />
		</div>
	);
}

const dayOfMonth = (day: string): string => String(Number(day.slice(8, 10)));
const weekdayOf = (day: string): string => formatCalendarDay(day).split(/[\s,]+/)[0] ?? "";
